import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ServerApiClient, ServerFirmPortfolio } from '../../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../../data/apiSession'

type FirmNavigationStatus = 'loading' | 'ready' | 'error'

export type CfoFirmNavigationValue = {
  api: ServerApiClient
  siteSlug: string
  portfolio: ServerFirmPortfolio | null
  clients: CfoFirmCompany[]
  status: FirmNavigationStatus
  error: string | null
  selectedWorkbookId: string | null
  selectedClient: CfoFirmCompany | null
  hasMultipleCompanies: boolean
  selectWorkbook: (workbookId: string) => void
  refreshPortfolio: () => Promise<ServerFirmPortfolio | null>
}

export type CfoFirmCompany = {
  workbook_id: string
  site_slug: string
  company_name: string | null
  client_name: string
  accounting_provider: string
  capabilities?: Record<string, boolean | string | null>
}

const CfoFirmNavigationContext = createContext<CfoFirmNavigationValue | null>(null)

const selectionStorageKey = (siteSlug: string) => `fg:cfo:selected-company:${siteSlug}`

const readStoredSelection = (siteSlug: string): string => {
  if (typeof window === 'undefined' || !siteSlug) return ''
  try {
    return String(window.localStorage.getItem(selectionStorageKey(siteSlug)) || '').trim()
  } catch {
    return ''
  }
}

const persistSelection = (siteSlug: string, workbookId: string) => {
  if (typeof window === 'undefined' || !siteSlug || !workbookId) return
  try {
    window.localStorage.setItem(selectionStorageKey(siteSlug), workbookId)
    // Keep the legacy workbook consumers aligned with the company selected in
    // the firm shell. The URL remains the canonical identity on company pages.
    window.localStorage.setItem('fusegrid_workbook_id', workbookId)
  } catch {
    // Selection persistence is a convenience; the in-memory context is enough.
  }
}

export function CfoFirmNavigationProvider({
  api,
  siteSlug,
  currentWorkbookId = null,
  loadPortfolio = false,
  children,
}: {
  api: ServerApiClient
  siteSlug: string
  currentWorkbookId?: string | null
  /** The cockpit needs metrics; workbook modules only need the lightweight
   * site summary for access count, labels, and routing. */
  loadPortfolio?: boolean
  children: ReactNode
}) {
  const [portfolio, setPortfolio] = useState<ServerFirmPortfolio | null>(null)
  const [clients, setClients] = useState<CfoFirmCompany[]>([])
  const [status, setStatus] = useState<FirmNavigationStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [selectedWorkbookId, setSelectedWorkbookId] = useState<string | null>(
    () => String(currentWorkbookId || readStoredSelection(siteSlug) || '').trim() || null,
  )

  const refreshPortfolio = useCallback(async (): Promise<ServerFirmPortfolio | null> => {
    if (!siteSlug) {
      setPortfolio(null)
      setStatus('error')
      setError('Firm site not found.')
      return null
    }
    setStatus('loading')
    setError(null)
    try {
      await ensureApiSessionReady()
      if (loadPortfolio) {
        const next = await api.getFirmPortfolio(siteSlug)
        setPortfolio(next)
        setClients(next.clients.map((client) => ({
          workbook_id: client.workbook_id,
          site_slug: client.site_slug || siteSlug,
          company_name: client.company_name || null,
          client_name: client.client_name,
          accounting_provider: client.accounting_provider || 'quickbooks',
          capabilities: client.capabilities || {},
        })))
        setStatus('ready')
        return next
      }
      const summary = await api.getSiteSummary(siteSlug)
      setPortfolio(null)
      setClients((summary.workbooks || [])
        .filter((workbook) => Boolean(workbook.accounting_provider || workbook.has_quickbooks_connection))
        .map((workbook) => ({
          workbook_id: workbook.id,
          site_slug: summary.site?.slug || siteSlug,
          company_name: workbook.accounting_company_name || workbook.qbo_company_name || null,
          client_name: workbook.accounting_company_name || workbook.qbo_company_name || workbook.name,
          accounting_provider: workbook.accounting_provider || 'quickbooks',
          capabilities: {},
        })))
      setStatus('ready')
      return null
    } catch (reason) {
      setPortfolio(null)
      setClients([])
      setStatus('error')
      setError(reason instanceof Error ? reason.message : String(reason))
      return null
    }
  }, [api, loadPortfolio, siteSlug])

  useEffect(() => {
    void refreshPortfolio()
  }, [refreshPortfolio])

  useEffect(() => {
    if (!clients.length) return
    const routeWorkbookId = String(currentWorkbookId || '').trim()
    const storedWorkbookId = readStoredSelection(siteSlug)
    const preferred = [routeWorkbookId, selectedWorkbookId || '', storedWorkbookId]
      .find((id) => id && clients.some((client) => client.workbook_id === id))
    const next = preferred || clients[0].workbook_id
    if (next !== selectedWorkbookId) setSelectedWorkbookId(next)
    persistSelection(siteSlug, next)
  }, [clients, currentWorkbookId, selectedWorkbookId, siteSlug])

  const selectWorkbook = useCallback((workbookId: string) => {
    const next = String(workbookId || '').trim()
    if (!next || !clients.some((client) => client.workbook_id === next)) return
    setSelectedWorkbookId(next)
    persistSelection(siteSlug, next)
  }, [clients, siteSlug])

  const selectedClient = clients.find((client) => client.workbook_id === selectedWorkbookId) || null

  const value = useMemo<CfoFirmNavigationValue>(() => ({
    api,
    siteSlug,
    portfolio,
    clients,
    status,
    error,
    selectedWorkbookId,
    selectedClient,
    hasMultipleCompanies: clients.length >= 2,
    selectWorkbook,
    refreshPortfolio,
  }), [api, clients, error, portfolio, refreshPortfolio, selectWorkbook, selectedClient, selectedWorkbookId, siteSlug, status])

  return <CfoFirmNavigationContext.Provider value={value}>{children}</CfoFirmNavigationContext.Provider>
}

export const useCfoFirmNavigationOptional = (): CfoFirmNavigationValue | null =>
  useContext(CfoFirmNavigationContext)

export const useCfoFirmNavigation = (): CfoFirmNavigationValue => {
  const value = useCfoFirmNavigationOptional()
  if (!value) throw new Error('CfoFirmNavigationProvider is required.')
  return value
}
